/* Created by Interactive 63 - https://interactive63.com */
/**
 * Sanitisation of untrusted SVG markup. Everything uploaded or loaded from a project file passes
 * through here before it can reach the live DOM: scripts, event handlers, foreign content,
 * external references and SMIL animation are all removed.
 */
import DOMPurify from 'dompurify';

const FORBIDDEN_TAGS = [
  'script',
  'foreignObject',
  'iframe',
  'embed',
  'object',
  'audio',
  'video',
  'animate',
  'animateMotion',
  'animateTransform',
  'set',
];

const ALLOWED_DATA_IMAGE = /^data:image\/(?:png|jpeg|gif|webp)[;,]/i;
// A url() whose target does not start with "#" points outside the document.
const EXTERNAL_URL = /url\(\s*(?!['"]?\s*#)/i;

/** Decodes CSS escapes such as "\\75rl", which could otherwise hide a url() from the check. */
function decodeCssEscapes(text: string): string {
  return text.replace(/\\([0-9a-f]{1,6})\s?/gi, (_match, hex: string) =>
    String.fromCodePoint(Math.min(parseInt(hex, 16), 0x10ffff) || 0xfffd),
  );
}

/** Removes @import rules and url() references that leave the document from stylesheet text. */
export function scrubStyleText(css: string): string {
  return decodeCssEscapes(css)
    .replace(/@import[^;{]*(;|$)/gi, '')
    .replace(/url\(\s*(?!['"]?\s*#)[^)]*\)/gi, 'none');
}

DOMPurify.addHook('uponSanitizeAttribute', (_node, data) => {
  const value = decodeCssEscapes(data.attrValue).trim();
  if (data.attrName === 'href' || data.attrName === 'xlink:href') {
    if (!value.startsWith('#') && !ALLOWED_DATA_IMAGE.test(value)) data.keepAttr = false;
    return;
  }
  if (EXTERNAL_URL.test(value)) data.keepAttr = false;
});

/**
 * Removes anything unsafe or unsuitable from SVG markup and returns well-formed XML. The result
 * is serialised from the sanitised DOM so namespaces are declared correctly.
 */
export function sanitiseSvg(markup: string): string {
  // With RETURN_DOM the result is the sanitised <body> element.
  const body = DOMPurify.sanitize(markup, {
    USE_PROFILES: { svg: true, svgFilters: true },
    ADD_TAGS: ['use'],
    FORBID_TAGS: FORBIDDEN_TAGS,
    ADD_ATTR: ['data-st-id', 'data-st-label', 'data-name', 'transform-origin'],
    WHOLE_DOCUMENT: false,
    RETURN_DOM: true,
  }) as unknown as HTMLElement;
  for (const style of Array.from(body.querySelectorAll('style'))) {
    style.textContent = scrubStyleText(style.textContent);
  }
  const svg = Array.from(body.children).find((child) => child.localName === 'svg');
  return svg ? new XMLSerializer().serializeToString(svg) : '';
}
